import { CONFIG } from '../config';
import { fetchJson } from '../lib/net';
import type { Comando, RawContent, VersionInfo } from './types';

/**
 * Origen del contenido. Producción: API CDN de Sanity (pública, sin token, solo documentos
 * publicados). Demo: public/demo/content.json con la misma forma.
 */
export interface ContentSource {
  /** Consulta barata cada 5 min: versión + comando remoto */
  fetchVersion(signal?: AbortSignal): Promise<VersionInfo>;
  /** Consulta completa: solo cuando la versión cambió */
  fetchContent(signal?: AbortSignal): Promise<RawContent>;
}

/** Proyectos de esta sede. Arequipa incluye los antiguos sin sede (creados antes de existir el campo) */
const SEDE_FILTER = CONFIG.sede === 'arequipa' ? '(!defined(sede) || sede == "arequipa")' : `sede == "${CONFIG.sede}"`;
const SETTINGS = `_id == "${CONFIG.settingsId}"`;

/** Documentos que definen la versión de ESTA sede: sus proyectos + su configuración */
const PUBLISHED = `((_type == "project" && ${SEDE_FILTER}) || ${SETTINGS}) && !(_id in path("drafts.**"))`;

/**
 * Versión = _updatedAt máximo + número de documentos. El conteo detecta borrados y
 * despublicaciones, que no mueven el _updatedAt máximo.
 */
const VERSION_GROQ = `{
  "u": *[${PUBLISHED}] | order(_updatedAt desc)[0]._updatedAt,
  "n": count(*[${PUBLISHED}])
}`;

export const VERSION_QUERY = `{
  "version": ${VERSION_GROQ},
  "cmd": *[${SETTINGS}][0]{comando, comandoId}
}`;

export const CONTENT_QUERY = `{
  "version": ${VERSION_GROQ},
  "settings": *[${SETTINGS}][0]{imagenesReposo, segundosInactividad, comando, comandoId},
  "projects": *[_type == "project" && ${SEDE_FILTER} && publicado == true && defined(portada.public_id)
               && (count(renders[defined(public_id)]) > 0 || count(fotosReales[defined(public_id)]) > 0)
               && !(_id in path("drafts.**"))] | order(orden asc, nombre asc){
    _id, _updatedAt, nombre, "slug": slug.current, ubicacion, estado, orden,
    portada, logo, renders, fotosReales, casasModelo, tiposCasa[]{_key, nombre, detalle, imagenes}, mapa
  }
}`;

/**
 * Subir este número cuando cambien las reglas con que la app arma el manifiesto (qué proyectos
 * entran, variantes, etc.): fuerza a los tótems a reconstruirlo aunque Sanity no haya cambiado.
 */
export const MANIFEST_RULES = 6;

export function versionString(v: { u: string | null; n: number }): string {
  return `r${MANIFEST_RULES}|${v.u ?? '0'}|${v.n}`;
}

function toVersionInfo(version: { u: string | null; n: number }, cmd: { comando?: Comando | null; comandoId?: string | null } | null): VersionInfo {
  return {
    version: versionString(version),
    command: cmd && cmd.comando && cmd.comando !== 'ninguno' && cmd.comandoId ? { comando: cmd.comando, comandoId: cmd.comandoId } : null,
  };
}

class SanitySource implements ContentSource {
  private url(query: string) {
    const { projectId, dataset, apiVersion } = CONFIG.sanity;
    return `https://${projectId}.apicdn.sanity.io/v${apiVersion}/data/query/${dataset}?query=${encodeURIComponent(query)}&perspective=published`;
  }
  async fetchVersion(signal?: AbortSignal) {
    const r = await fetchJson<{ result: { version: { u: string | null; n: number }; cmd: { comando?: Comando; comandoId?: string } | null } }>(
      this.url(VERSION_QUERY),
      { signal },
    );
    return toVersionInfo(r.result.version, r.result.cmd);
  }
  async fetchContent(signal?: AbortSignal) {
    const r = await fetchJson<{ result: RawContent }>(this.url(CONTENT_QUERY), { signal });
    return r.result;
  }
}

class DemoSource implements ContentSource {
  async fetchVersion(signal?: AbortSignal) {
    const c = await fetchJson<RawContent>('/demo/content.json', { signal });
    return toVersionInfo(c.version, c.settings);
  }
  async fetchContent(signal?: AbortSignal) {
    return fetchJson<RawContent>('/demo/content.json', { signal });
  }
}

export function createContentSource(): ContentSource {
  return CONFIG.source === 'sanity' ? new SanitySource() : new DemoSource();
}
